import { SuiBadgeVariant } from '@sui/badge';

/**
 * Shared nomenclature -> sui-badge variant maps for the Source Catalog
 * explorers (Data Providers, Sitemaps, Harvesting Log, ...). Feature-specific
 * maps (HTTP log outcome ids, remediation run statuses) stay in their feature.
 *
 * All lookups go through badgeVariantKey(): lowercase, spaces -> underscores,
 * String()-coerced - the backend's nom-miss fallback can deliver the raw id as
 * a number, and a throw inside an ag-grid cell renderer aborts the whole row
 * render, taking every later cell (incl. the pinned actions column) with it.
 */
export function badgeVariantKey(value: string | null | undefined): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

/**
 * Variant lookup key for a nomenclature row: prefer the stable `alias`, fall
 * back to the normalised display name. We colour by alias/name, never by a
 * literal id.
 */
export function nomVariantKey(nom: {
  alias?: string | null;
  name?: string | null;
}): string {
  return badgeVariantKey(nom.alias ?? nom.name);
}

/** Infrastructure finding / host compliance severity -> variant (Findings and Hosts grids). */
export function infraSeverityBadgeVariant(item: {
  alias?: string | null;
  name?: string | null;
}): SuiBadgeVariant {
  switch (nomVariantKey(item)) {
    case 'critical':
      return 'danger';
    case 'warning':
      return 'warning';
    case 'normal':
      return 'success';
    default:
      return 'info';
  }
}

/** Object lifecycle status / Data Provider / Sitemap mode -> variant. */
export function modeBadgeVariant(nameOrAlias: string): SuiBadgeVariant {
  switch (badgeVariantKey(nameOrAlias)) {
    case 'active':
      return 'success';
    case 'pending':
    case 'review':
      return 'warning';
    case 'inactive':
      return 'inactive';
    case 'deleted':
      return 'danger';
    case 'archived':
      return 'archived';
    default:
      return 'neutral';
  }
}

/**
 * Variant -> Tier-2 fill token, for chart/donut/bar fills that mirror badge
 * variants (data-view donut segments, legends, status bars, summary tiles).
 */
export const BADGE_VARIANT_FILL_TOKEN: Record<SuiBadgeVariant, string> = {
  success: '--color-feedback-success-solid',
  brand: '--color-static-brand',
  warning: '--color-feedback-warning-solid',
  danger: '--color-feedback-danger-solid',
  info: '--color-feedback-info-solid',
  neutral: '--color-feedback-neutral-solid',
  inactive: '--color-status-archived-solid',
  archived: '--color-status-archived-solid',
};

/**
 * Legacy backend action tag style -> variant. The user-action log endpoints
 * (User Activity Logs, Import Content Logs) classify each of the 70+ action
 * nomenclature entries by severity server-side and ship the result as a
 * Bootstrap-era class name in `action.style` ('tag-success' for View,
 * 'tag-danger' for Delete, ...); only the class-name vocabulary is mapped
 * here, the severity judgement stays on the backend.
 */
export function actionTagVariant(
  style: string | null | undefined,
): SuiBadgeVariant {
  switch (style) {
    case 'tag-success':
      return 'success';
    case 'tag-danger':
      return 'danger';
    case 'tag-warning':
      return 'warning';
    case 'tag-info':
      return 'info';
    case 'tag-primary':
      return 'brand';
    default:
      // 'tag-default' and anything unknown.
      return 'neutral';
  }
}

/** Harvest status (Harvesting Log status / Data Provider last_status) -> variant. */
export function harvestStatusVariant(nameOrAlias: string): SuiBadgeVariant {
  switch (badgeVariantKey(nameOrAlias)) {
    case 'success':
      return 'success';
    case 'partial':
      return 'warning';
    case 'error':
    case 'auth_error':
      return 'danger';
    case 'rate_limited':
      return 'warning';
    case 'skipped':
      return 'inactive';
    default:
      return 'neutral';
  }
}
